import { ingredientDetails } from '../ingredientLabel';
import { useSuppliers, useIngredients, useRecipes, useSaveMenuItem, useSettings, useMenuItems } from '../api/hooks';
import type { ConfirmRecipeProposalInput, ConfirmRecipeProposalResponse } from '../api/contract';
import { useEffect, useMemo, useState } from 'react';
import {
  buildContext,
  clasificarFaltantes,
  colchonDeCuenta,
  commissionOnGross,
  computeRecipeCostPartial,
  aliasKey,
  dimensionOf,
  estimatePrice,
  listaConArticulos,
  matchIngredient,
  normalizeIngredientName,
  planRecipeProposal,
  platoDesdePreparacion,
  sugerenciasPara,
  toBase,
  type ContextoAlta,
  type Faltante,
  type Ingredient,
  type IngredientMatch,
  type Recipe,
  type RecipeCost,
  type RecipeProposal,
  type RecipeProposalPlan,
  type Sugerencia,
  type Unit,
} from '@alexiarest/shared';
import { useConfirmRecipeProposal, useProposeRecipe, useMe, useProposeRecipePhoto } from '../api/hooks';
import { prepararImagen, ACCEPT_ESCANER } from '../imagenEscaner';
import { useDictadoWhisper } from '../hooks/useDictadoWhisper';
import { api, isDemoMode } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import type { RecipeProposalResponse } from '../api/contract';
import { formatDecimalInput, formatMoney, formatMoneyInput, formatPct, formatQty, parseDecimal } from '../format';
import { unitLabel } from '../labels';
import { Drawer } from './Drawer';
import { SmartSelect } from './Field';
import { UnitSelectField } from './UnitSelectField';
import { useToast } from './Toast';
import { QueEs } from './QueEs';
import './nuevo-plato.css';

/**
 * Nuevo plato en dos pasos (rediseño de Alex, 10 oct 2026; revisión del 12 oct).
 *
 *   Paso 1 · Cuéntamelo: dictar (con Whisper), escribir o una foto. O
 *            «Prefiero llenarlo yo», que es el paso 2 vacío («Tu plato» o
 *            «Tu preparación», sin «Paso 2 de 2»).
 *   Paso 2 · Revisa lo que entendí: una TARJETA por cada cosa. Si el dictado
 *            trae una preparación y el plato que la usa (la masa de croquetas
 *            y la porción de 8), salen las dos y «Guardar los dos»: primero se
 *            guarda la preparación y el plato la referencia. Si solo dicta la
 *            masa, al guardar se pregunta «¿Cuántas lleva la porción?» y se
 *            crea también el plato.
 *
 * Nunca se crea un plato vacío «para costearlo después»: o lleva
 * ingredientes, o es un plato de la carta con su precio («Sin terminar»).
 */

interface EditableLine {
  isSubrecipe?: boolean;
  rawText: string;
  ingredientName: string;
  /** Ingrediente, preparación (`isSubrecipe`) o `card:N` (otra tarjeta de este dictado). */
  ingredientId: string;
  qty: string;
  unit: Unit;
  wastePct: string;
  match: IngredientMatch;
  estimated: boolean;
  conversionNote?: string;
  /** El usuario la dio por buena («Está bien así» o eligió el ingrediente). */
  revisada?: boolean;
  /** Fase 7: lo que dijo, tal cual («harina»), para recordarlo como alias. */
  dicho?: string;
  /** Eligió el ingrediente a mano: se ofrece «La próxima vez que digas…». */
  aMano?: boolean;
  /** «La próxima vez que digas «harina», usar esta sin preguntar» (marcada por defecto). */
  recordar?: boolean;
  /** Preparación que ya existía en la cuenta («ya la tenías»). */
  yaLaTenias?: boolean;
  /** La cantidad la propuso Alexia (no la dijo): «(cantidad propuesta)». */
  propuesta?: boolean;
  /** «450 de harina»: dijo la cantidad sin unidad y Alexia supuso gramos. */
  supuseUnidad?: boolean;
}

interface Tarjeta {
  clave: string;
  draftId?: string;
  /** Receta que se actualiza (un borrador que completa una ficha). */
  recipeId?: string;
  /** Ya guardada en este intento: un reintento no la duplica. */
  guardadaId?: string;
  name: string;
  group: string;
  esPlato: boolean;
  marcadoPorAlexia: boolean;
  porque?: string;
  price: string;
  servings: string;
  yieldQty: string;
  yieldUnit: Unit;
  unitWeightG: string;
  lines: EditableLine[];
  abierta: number | null;
  colchonPropio: string | null;
  steps?: string[];
  sugerenciasVistas: Sugerencia[];
  creandoGrupo?: boolean;
  busqueda: string;
}

const NUEVO = '__new__';
const CARD = 'card:';
const vivoId = (k: number) => `__nuevo-plato-${k}__`;
const EJEMPLO = 'Tacos de camarón: salen 3 por porción. Llevan 120 g de camarón, 3 tortillas de maíz, 40 g de col morada y 30 g de crema de chipotle. Los vendo a 145.';
/**
 * El ejemplo del dictado, en la moneda de la cuenta (revisión del 14 oct
 * 2026: en una cuenta en euros decía «Los vendo a 145»). En euros, un plato
 * de allí con su precio en euros; en el resto, el de pesos.
 */
export function ejemploDePlato(moneda: string): string {
  if (moneda === 'EUR') return `Patatas bravas: salen 4 porciones. Llevan 1 kg de papa, 200 ml de salsa brava y 100 ml de alioli. Las vendo a ${formatMoney(6.5)}.`;
  return EJEMPLO;
}
const EJEMPLO_PREP = 'Salsa brava: hago 2 litros con 1 kg de tomate, 2 cebollas, 30 g de pimentón y 200 ml de aceite.';
const GRUPOS_DE_SERIE = ['Para compartir', 'Principales', 'Postres', 'Bebidas'];
const PALABRAS = ['', 'uno', 'dos', 'tres'];

function MicIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="nuevo-plato__mic-icon">
      <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" />
      <path d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v4M8.5 21h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/** Con miles («1,250» en México) y sin NaN escondidos: lo que no se entiende es NaN y no pasa los «> 0». */
const num = (value: string): number => parseDecimal(String(value), NaN);
const tarjetaDe = (line: EditableLine): number | undefined => (line.ingredientId.startsWith(CARD) ? Number(line.ingredientId.slice(CARD.length)) : undefined);

/** La unidad en la que se usa algo, según cómo se compra o cuánto rinde. */
const unidadDeUso = (u: Unit | undefined): Unit => {
  try { return u ? (dimensionOf(u) === 'mass' ? 'g' : dimensionOf(u) === 'volume' ? 'ml' : 'pz') : 'g'; } catch { return 'g'; }
};

/** Gramos (o ml) de una cantidad; sin dato si va en piezas. */
const gramosDe = (qty: number, unit: Unit): number | undefined => {
  try { return qty > 0 && dimensionOf(unit) !== 'count' ? toBase(qty, unit) : undefined; } catch { return undefined; }
};

/**
 * Fase 7 · alias. Se ofrece recordar cuando el usuario eligió a mano un
 * ingrediente que no se llama como lo dijo: «harina» → «Harina de trigo».
 */
function ofrecerRecordar(line: EditableLine, ingredients: Ingredient[]): boolean {
  if (!line.aMano || line.isSubrecipe || !line.dicho?.trim() || !line.ingredientId || line.ingredientId === NUEVO) return false;
  const elegido = ingredients.find((i) => i.ingredientId === line.ingredientId);
  return Boolean(elegido) && aliasKey(line.dicho) !== aliasKey(elegido!.name);
}
const debeRecordar = (line: EditableLine, ingredients: Ingredient[]): boolean => ofrecerRecordar(line, ingredients) && line.recordar !== false;

/** Una fila pide revisión si falta algo, si Alexia dudó o si lo calculó ella. */
function necesitaRevision(line: EditableLine): boolean {
  if (line.revisada) return !line.ingredientId || !(num(line.qty) > 0);
  return !line.ingredientId || !(num(line.qty) > 0) || line.match.kind === 'ambiguous' || line.estimated;
}

const lineaVacia = (): EditableLine => ({ rawText: '', ingredientName: '', ingredientId: '', qty: '', unit: 'g', wastePct: '0', match: { kind: 'none', candidates: [] }, estimated: false });

function tarjetaVacia(esPlato: boolean, clave = 'mano'): Tarjeta {
  return {
    clave, name: '', group: '', esPlato, marcadoPorAlexia: false, price: '', servings: '1', yieldQty: '', yieldUnit: 'pz', unitWeightG: '',
    lines: [], abierta: null, colchonPropio: null, sugerenciasVistas: [], busqueda: '',
  };
}

/** Orden de guardado: lo que otra tarjeta usa va antes. */
function ordenDeGuardado(lista: Tarjeta[]): number[] {
  const orden: number[] = [];
  const visitar = (k: number, camino: Set<number>) => {
    if (orden.includes(k) || camino.has(k)) return;
    camino.add(k);
    for (const l of lista[k].lines) { const j = tarjetaDe(l); if (j != null && lista[j]) visitar(j, camino); }
    orden.push(k);
  };
  lista.forEach((_, k) => visitar(k, new Set()));
  return orden;
}

/** «de el alioli» → «del alioli». */
const deLa = (lista: string): string => (lista.startsWith('el ') ? `del ${lista.slice(3)}` : `de ${lista}`);

function contar(n: number, una: string, varias: string): string {
  return `${n} ${n === 1 ? una : varias}`;
}

export function TellAlexiaRecipeDrawer({
  open,
  onClose,
  onSaved,
  initialProposal,
  confirmProposal,
  targetRecipeId,
  initialMode,
  initialText,
  requireIngredients = false,
  tipo = 'plato',
}: {
  initialText?: string;
  /** El alta necesita un plato con ingredientes para activar el catálogo. */
  requireIngredients?: boolean;
  targetRecipeId?: string;
  /** Pestaña con la que se abre (el alta ofrece las tres como botones). */
  initialMode?: 'text' | 'dictate' | 'photo';
  /** Desde Preparaciones: «Prefiero llenarlo yo» abre una preparación y la duda se resuelve hacia preparación. */
  tipo?: ContextoAlta;
  open: boolean;
  onClose: () => void;
  onSaved: (recipeId: string) => void;
  initialProposal?: RecipeProposalResponse;
  confirmProposal?: (input: ConfirmRecipeProposalInput) => Promise<ConfirmRecipeProposalResponse>;
}): JSX.Element | null {
  const propose = useProposeRecipe();
  const photo = useProposeRecipePhoto();
  const [readingPhoto, setReadingPhoto] = useState(false);
  const me = useMe();
  const dictation = useDictadoWhisper(me.data?.tenant.locale ?? navigator.language ?? 'es-MX');
  const confirm = useConfirmRecipeProposal(confirmProposal);
  const saveMenuItem = useSaveMenuItem();
  const toast = useToast();
  const qc = useQueryClient();
  const suppliers = useSuppliers().data ?? [];
  const ingredients = useIngredients().data ?? [];
  const recipes = useRecipes().data ?? [];
  const menuItems = useMenuItems().data ?? [];
  const settings = useSettings().data;
  const [text, setText] = useState(initialText ?? '');
  const modoInicial = (): 'text' | 'dictate' | 'photo' => (initialMode ?? 'dictate') === 'dictate' && !dictation.supported ? 'text' : initialMode ?? 'dictate';
  const [mode, setMode] = useState<'text' | 'dictate' | 'photo'>(modoInicial);
  useEffect(() => { if (open) setMode(modoInicial()); }, [open, initialMode]);
  const [tarjetas, setTarjetas] = useState<Tarjeta[]>([]);
  /** Con muchas recetas (una carta en foto) se revisan de una en una. */
  const [pendientes, setPendientes] = useState<Array<{ proposal: RecipeProposal; plan: RecipeProposalPlan; draftId?: string; indice: number }>>([]);
  const [totalPropuestas, setTotalPropuestas] = useState(1);
  /** Recetas del dictado ya guardadas, por su índice en la propuesta (para enlazar las que vienen). */
  const [guardadas, setGuardadas] = useState<Record<number, string>>({});
  const [aMano, setAMano] = useState(false);
  const [textoFuente, setTextoFuente] = useState('');
  const [preguntaPorcion, setPreguntaPorcion] = useState<{ cantidad: string; precio: string } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const enPaso2 = tarjetas.length > 0;

  // El texto del dictado se añade a lo que ya hubiera escrito, y se puede corregir.
  useEffect(() => {
    if (dictation.transcript) setText((prev) => [prev.trim(), dictation.transcript].filter(Boolean).join(prev.trim() ? ' ' : ''));
  }, [dictation.transcript]);
  useEffect(() => { if (!open) dictation.stop(); }, [open]);

  const preparacionesDeLaCuenta = (catalogo?: RecipeProposalResponse['recipeCatalog']) => {
    const propias = recipes.filter((r) => r.active && r.kind === 'subreceta');
    return (propias.length ? propias : (catalogo ?? []) as Array<Pick<Recipe, 'recipeId' | 'name'>>).map((r) => ({ ingredientId: r.recipeId, name: r.name }));
  };

  /** De una propuesta de Alexia a una tarjeta del paso 2. */
  const aTarjeta = (proposal: RecipeProposal, plan: RecipeProposalPlan, extra: { draftId?: string; recipeCatalog?: RecipeProposalResponse['recipeCatalog']; base: number; hechas: Record<number, string> }, k: number): Tarjeta => {
    const preps = preparacionesDeLaCuenta(extra.recipeCatalog);
    return {
      ...tarjetaVacia(!proposal.isSubrecipe, `${k}-${proposal.name}`),
      draftId: extra.draftId,
      recipeId: proposal.recipeId ?? (k === 0 && extra.base === 0 ? targetRecipeId : undefined),
      name: proposal.name,
      group: proposal.group ?? '',
      marcadoPorAlexia: Boolean(proposal.name) && !targetRecipeId,
      porque: proposal.porque,
      price: proposal.sellingPriceGross ? formatMoneyInput(proposal.sellingPriceGross) : '',
      servings: proposal.servings && !proposal.isSubrecipe ? formatDecimalInput(proposal.servings) : '1',
      yieldQty: proposal.yieldQty ? formatDecimalInput(proposal.yieldQty) : proposal.isSubrecipe && proposal.servings ? formatDecimalInput(proposal.servings) : '',
      yieldUnit: proposal.yieldUnit ?? 'pz',
      unitWeightG: proposal.unitWeightG ? formatDecimalInput(proposal.unitWeightG) : '',
      steps: proposal.steps,
      lines: plan.lines.map((line): EditableLine => {
        // La otra receta de este mismo dictado (la masa que usa la porción).
        if (line.fromProposal != null) {
          const global = line.fromProposal;
          const enPantalla = global - extra.base;
          const id = extra.hechas[global] ?? (enPantalla >= 0 ? `${CARD}${enPantalla}` : '');
          return {
            isSubrecipe: true, rawText: line.rawText, ingredientName: line.ingredientName, dicho: line.ingredientName, ingredientId: id,
            qty: line.qty != null ? formatDecimalInput(line.qty) : '', unit: line.unit ?? 'pz', wastePct: '0', match: { kind: 'none', candidates: [] }, estimated: false,
          };
        }
        let isSub = Boolean(line.isSubrecipe);
        let match: IngredientMatch = isSub ? matchIngredient(line.ingredientName, preps) : line.match;
        let yaLaTenias = isSub && (match.kind === 'exact' || match.kind === 'fuzzy');
        // «Con alioli»: si ya es una preparación tuya, se usa esa («ya la tenías»).
        if (!isSub && match.kind !== 'exact' && match.kind !== 'fuzzy') {
          const comoPrep = matchIngredient(line.ingredientName, preps);
          if (comoPrep.kind === 'exact' || comoPrep.kind === 'fuzzy') { isSub = true; match = comoPrep; yaLaTenias = true; }
        }
        const elegido = match.kind === 'exact' || match.kind === 'fuzzy' ? match.candidates[0].ingredientId : '';
        let qty = !line.issue && line.qty != null ? formatDecimalInput(line.qty) : '';
        let unit: Unit = line.issue ? 'g' : line.unit ?? 'g';
        let propuesta = false;
        if (yaLaTenias && !qty) {
          const prep = recipes.find((r) => r.recipeId === elegido);
          unit = unidadDeUso(prep?.yieldUnit);
          qty = unit === 'pz' ? '1' : '30';
          propuesta = true;
        }
        return {
          isSubrecipe: isSub,
          rawText: line.rawText,
          ingredientName: line.ingredientName,
          dicho: line.ingredientName,
          ingredientId: line.ingredientId ?? (elegido || (match.kind === 'none' && !isSub ? NUEVO : '')),
          qty,
          unit,
          wastePct: line.wastePct != null && !isSub ? formatDecimalInput(line.wastePct) : '0',
          match,
          estimated: propuesta || Boolean(line.estimated.qty || line.estimated.unit || line.estimated.wastePct),
          conversionNote: line.conversionNote,
          yaLaTenias,
          propuesta,
          supuseUnidad: !propuesta && !line.conversionNote && Boolean(line.estimated.unit) && !line.estimated.qty,
        };
      }),
    };
  };

  const recibir = (next: RecipeProposalResponse, fuente: string) => {
    const lista = (next.proposals?.length ? next.proposals : [next.proposal]);
    const catalog = next.catalog;
    const entradas = lista.map((proposal, i) => ({ proposal, plan: i === 0 && proposal === next.proposal ? next.plan : planRecipeProposal(proposal, catalog), draftId: i === 0 ? next.draftId ?? next.draftIds?.[0] : next.draftIds?.[i], indice: i }));
    // Hasta tres se revisan juntas («Guardar los dos»); más (una carta entera), de una en una.
    const juntas = entradas.length <= 3 ? entradas : entradas.slice(0, 1);
    setPendientes(entradas.slice(juntas.length));
    setTotalPropuestas(entradas.length);
    setGuardadas({});
    setAMano(false);
    setTextoFuente(fuente);
    setPreguntaPorcion(null);
    setTarjetas(juntas.map((e, k) => aTarjeta(e.proposal, e.plan, { draftId: e.draftId, recipeCatalog: next.recipeCatalog, base: 0, hechas: {} }, k)));
  };
  useEffect(() => {
    if (initialProposal && open) recibir({ ...initialProposal, proposals: initialProposal.proposals ?? [initialProposal.proposal] }, '');
  }, [initialProposal, open]);

  const colchonCuenta = colchonDeCuenta(settings);
  const colchonDe = (t: Tarjeta) => (t.colchonPropio != null && t.colchonPropio !== '' ? Math.min(Math.max(num(t.colchonPropio) || 0, 0), 99) : colchonCuenta);
  const pais = me.data?.tenant.country ?? 'ES';
  const moneda = me.data?.tenant.currency ?? 'EUR';

  /*
   * Costo en vivo: la misma cuenta que la ficha, sobre recetas de mentira con
   * lo que hay en pantalla (una por tarjeta, y el plato usa la de la masa).
   */
  const vivo = useMemo(() => {
    const recetas: Recipe[] = tarjetas.map((t, k) => ({
      recipeId: vivoId(k), tenantId: '', name: t.name || 'Nuevo plato', kind: t.esPlato ? 'plato' : 'subreceta', active: true,
      portions: t.esPlato ? Math.max(1, Math.round(num(t.servings)) || 1) : 1,
      yieldQty: t.esPlato ? 1 : num(t.yieldQty) > 0 ? num(t.yieldQty) : 1, yieldUnit: t.esPlato ? 'pz' : t.yieldUnit,
      ...(!t.esPlato && t.yieldUnit === 'pz' && num(t.unitWeightG) > 0 ? { unitWeightG: num(t.unitWeightG) } : {}),
      safetyMarginPct: colchonDe(t), useYieldCostAsBase: !t.esPlato, updatedAt: '',
      components: t.lines.filter((l) => l.ingredientId && l.ingredientId !== NUEVO && num(l.qty) > 0 && (tarjetaDe(l) == null || tarjetas[tarjetaDe(l)!])).map((l) => {
        const j = tarjetaDe(l);
        return {
          ref: j != null ? { kind: 'recipe' as const, recipeId: vivoId(j) } : l.isSubrecipe ? { kind: 'recipe' as const, recipeId: l.ingredientId } : { kind: 'ingredient' as const, ingredientId: l.ingredientId },
          block: 'ingrediente' as const, qty: num(l.qty), unit: l.unit, wastePct: l.isSubrecipe ? 0 : Math.min(Math.max(num(l.wastePct) || 0, 0), 99),
        };
      }),
    }));
    const ctx = buildContext(ingredients, [...recipes, ...recetas]);
    const porTarjeta: Array<{ cost?: RecipeCost; porLinea: Map<number, number>; importantes: string[]; menores: string[] }> = [];
    const calcular = (k: number, camino: Set<number>): (typeof porTarjeta)[number] => {
      if (porTarjeta[k]) return porTarjeta[k];
      const t = tarjetas[k];
      const porLinea = new Map<number, number>();
      let cost: RecipeCost | undefined;
      const validas = t.lines.map((l, i) => ({ l, i })).filter(({ l }) => l.ingredientId && l.ingredientId !== NUEVO && num(l.qty) > 0 && (tarjetaDe(l) == null || tarjetas[tarjetaDe(l)!]));
      if (validas.length) {
        try {
          cost = computeRecipeCostPartial(vivoId(k), ctx);
          cost.lines.forEach((line, n) => { const original = validas[n]; if (original) porLinea.set(original.i, line.pending ? NaN : line.cost); });
        } catch { cost = undefined; }
      }
      // Qué falta y cuánto importa (punto 2: sin «te deja» falso).
      const sinPrecio = new Set(cost?.missingIngredientIds ?? []);
      const gramosTotales = t.lines.reduce((s, l) => s + (gramosDe(num(l.qty), l.unit) ?? 0), 0);
      const faltantes: Faltante[] = [];
      const anotados = new Set<string>();
      t.lines.forEach((l) => {
        const nuevo = l.ingredientId === NUEVO;
        if (!nuevo && !(l.ingredientId && sinPrecio.has(l.ingredientId))) return;
        const ing = ingredients.find((i) => i.ingredientId === l.ingredientId);
        const nombre = (ing?.name ?? l.ingredientName).trim();
        if (!nombre || anotados.has(nombre)) return;
        anotados.add(nombre);
        const qty = num(l.qty);
        const ref = qty > 0 ? estimatePrice(nombre, l.unit, pais, moneda) : null;
        faltantes.push({ nombre, gramos: gramosDe(qty, l.unit), ...(ref ? { costoEstimado: ref.unitPrice * qty } : {}), ...(qty > 0 ? {} : { sinCantidad: true }) });
      });
      // Lo que falta dentro de una preparación ya guardada: no se sabe cuánto pesa, así que importa.
      const deFuera = [...sinPrecio].filter((id) => !t.lines.some((l) => l.ingredientId === id)).map((id) => ingredients.find((i) => i.ingredientId === id)?.name).filter((n): n is string => Boolean(n));
      const { importantes, menores } = clasificarFaltantes(faltantes, { costoConocido: cost?.totalBatchCost ?? 0, gramosTotales });
      const imp = new Set([...importantes, ...deFuera]);
      const men = new Set(menores);
      camino.add(k);
      for (const l of t.lines) {
        const j = tarjetaDe(l);
        if (j == null || !tarjetas[j] || camino.has(j)) continue;
        const sub = calcular(j, camino);
        sub.importantes.forEach((n) => imp.add(n));
        sub.menores.forEach((n) => men.add(n));
      }
      porTarjeta[k] = { cost, porLinea, importantes: [...imp], menores: [...men].filter((n) => !imp.has(n)) };
      return porTarjeta[k];
    };
    tarjetas.forEach((_, k) => calcular(k, new Set()));
    return porTarjeta;
  }, [tarjetas, ingredients, recipes, colchonCuenta, pais, moneda]);

  /** Aceite o pan que no dijo: el de tu catálogo y lo que suele costar por porción. */
  const sugerido = useMemo(() => {
    const busca = (re: RegExp, prefer: RegExp, no?: RegExp) => {
      const todos = ingredients.filter((i) => i.active && re.test(i.name) && !(no && no.test(i.name)));
      return todos.find((i) => prefer.test(i.name)) ?? todos[0];
    };
    const costoDe = (ing: Ingredient | undefined, qty: number, unit: Unit, ref: string): number | undefined => {
      if (ing) {
        try {
          const receta: Recipe = { recipeId: '__sugerencia__', tenantId: '', name: 's', kind: 'plato', active: true, portions: 1, yieldQty: 1, yieldUnit: 'pz', safetyMarginPct: 0, useYieldCostAsBase: false, updatedAt: '',
            components: [{ ref: { kind: 'ingredient', ingredientId: ing.ingredientId }, block: 'ingrediente', qty, unit, wastePct: 0 }] };
          const c = computeRecipeCostPartial('__sugerencia__', buildContext(ingredients, [receta]));
          if (c.status !== 'partial') return c.costPerPortion;
        } catch { /* sin precio */ }
      }
      const e = estimatePrice(ref, unit, pais, moneda);
      return e ? e.unitPrice * qty : undefined;
    };
    const aceite = busca(/\baceite\b/i, /girasol|vegetal|fre[ií]r|canola|ma[ií]z/i);
    const pan = busca(/\bpan\b/i, /hamburguesa|bollo|telera|bolillo|baguette/i, /rallado|molido/i);
    return {
      aceite: { ing: aceite, qty: 15, unit: 'ml' as Unit, nombre: 'Aceite para freír', costo: costoDe(aceite, 15, 'ml', 'aceite de girasol') },
      pan: { ing: pan, qty: 1, unit: 'pz' as Unit, nombre: 'Pan', costo: costoDe(pan, 1, 'pz', 'pan') },
    };
  }, [ingredients, pais, moneda]);

  if (!open) return null;

  const generate = (recipeText = text) => propose.mutate({ text: recipeText, contexto: tipo }, { onSuccess: (r) => recibir(r, recipeText), onError: (error) => toast.fail(error) });
  const readPhoto = async (file: File) => {
    setReadingPhoto(true);
    try {
      const prepared = await prepararImagen(file, { maxLado: 2000, maxPdfBytes: 8_000_000, errorPdf: 'El PDF debe pesar como máximo 8 MB.', errorTamanyo: 'La foto es demasiado grande. Prueba una foto de cada página.' });
      photo.mutate(prepared, { onSuccess: (r) => recibir(r, ''), onError: (error) => toast.fail(error) });
    } catch (error) { toast.fail(error); }
    finally { setReadingPhoto(false); }
  };
  /** «Prefiero llenarlo yo»: la misma pantalla, vacía, solo con el buscador. */
  const llenarlaYo = () => {
    setPendientes([]);
    setTotalPropuestas(1);
    setGuardadas({});
    setAMano(true);
    setTextoFuente('');
    setPreguntaPorcion(null);
    setTarjetas([tarjetaVacia(tipo !== 'preparacion')]);
  };
  const volverAlPaso1 = () => { setTarjetas([]); setPendientes([]); setAMano(false); setPreguntaPorcion(null); };
  const closeDrawer = () => { dictation.stop(); onClose(); };

  const setTarjeta = (k: number, patch: Partial<Tarjeta> | ((t: Tarjeta) => Partial<Tarjeta>)) =>
    setTarjetas((current) => current.map((t, i) => (i === k ? { ...t, ...(typeof patch === 'function' ? patch(t) : patch) } : t)));
  const setLine = (k: number, index: number, patch: Partial<EditableLine>) =>
    setTarjeta(k, (t) => ({ lines: t.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }));
  const removeLine = (k: number, index: number) => setTarjeta(k, (t) => ({ lines: t.lines.filter((_, i) => i !== index), abierta: null }));

  /** Elegir un ingrediente (o una preparación) para la fila. */
  const elegir = (k: number, index: number, value: string) => {
    const line = tarjetas[k].lines[index];
    if (value === NUEVO) { setLine(k, index, { ingredientId: NUEVO, isSubrecipe: false, revisada: true }); return; }
    if (value.startsWith('rec:')) {
      const receta = recipes.find((r) => r.recipeId === value.slice(4));
      setLine(k, index, { ingredientId: value.slice(4), isSubrecipe: true, ingredientName: receta?.name ?? line.ingredientName, revisada: true, wastePct: '0' });
      return;
    }
    const ingrediente = ingredients.find((i) => i.ingredientId === value);
    setLine(k, index, { ingredientId: value, isSubrecipe: false, ingredientName: ingrediente?.name ?? line.ingredientName, revisada: true, aMano: Boolean(value), recordar: line.recordar ?? true });
    // Elegido y con cantidad: la fila ya está lista y se cierra.
    if (value && num(line.qty) > 0 && !line.estimated) setTarjeta(k, { abierta: -1 });
  };

  /** El buscador de abajo: elegir algo agrega la fila y la abre para poner la cantidad. */
  const agregarDesdeBuscador = (k: number, value: string) => {
    const t = tarjetas[k];
    let linea: EditableLine;
    if (value === NUEVO) {
      const nombre = t.busqueda.trim();
      linea = { ...lineaVacia(), ingredientId: NUEVO, ingredientName: nombre, dicho: nombre, revisada: true };
    } else if (value.startsWith('rec:')) {
      const receta = recipes.find((r) => r.recipeId === value.slice(4));
      linea = { ...lineaVacia(), ingredientId: value.slice(4), isSubrecipe: true, ingredientName: receta?.name ?? '', unit: unidadDeUso(receta?.yieldUnit), revisada: true };
    } else if (value.startsWith(CARD)) {
      const otra = tarjetas[Number(value.slice(CARD.length))];
      linea = { ...lineaVacia(), ingredientId: value, isSubrecipe: true, ingredientName: otra?.name ?? '', unit: unidadDeUso(otra?.yieldUnit), revisada: true };
    } else if (value) {
      const ing = ingredients.find((i) => i.ingredientId === value);
      linea = { ...lineaVacia(), ingredientId: value, ingredientName: ing?.name ?? '', unit: unidadDeUso(ing?.purchaseUnit), match: { kind: 'exact', candidates: [{ ingredientId: value, name: ing?.name ?? '', score: 1 }] }, revisada: true };
    } else return;
    setTarjeta(k, (cur) => ({ lines: [...cur.lines, linea], abierta: cur.lines.length, busqueda: '' }));
  };

  const aplicarSugerencia = (k: number, s: Sugerencia) => {
    const d = sugerido[s];
    const porciones = Math.max(1, Math.round(num(tarjetas[k].servings)) || 1);
    const linea: EditableLine = d.ing
      ? { ...lineaVacia(), ingredientId: d.ing.ingredientId, ingredientName: d.ing.name, match: { kind: 'exact', candidates: [{ ingredientId: d.ing.ingredientId, name: d.ing.name, score: 1 }] }, qty: formatDecimalInput(d.qty * porciones), unit: d.unit, estimated: true, propuesta: true }
      : { ...lineaVacia(), ingredientId: NUEVO, ingredientName: d.nombre, dicho: d.nombre, qty: formatDecimalInput(d.qty * porciones), unit: d.unit, estimated: true, propuesta: true };
    setTarjeta(k, (t) => ({ lines: [...t.lines, linea], sugerenciasVistas: [...t.sugerenciasVistas, s] }));
  };

  const validar = (lista: Tarjeta[]): boolean => {
    if (requireIngredients && !lista.some((t) => t.esPlato)) {
      toast.notify('Para empezar, crea un plato de tu carta (no una preparación).', 'error');
      return false;
    }
    for (const [k, t] of lista.entries()) {
      if (t.guardadaId) continue;
      const necesitaLineas = requireIngredients || !t.esPlato || !(num(t.price) > 0) || Boolean(t.recipeId);
      const conLineas = t.lines.filter((l) => l.ingredientName.trim() || l.ingredientId);
      if (necesitaLineas && !conLineas.length) { toast.notify('Agrega al menos un ingrediente. Así sabrás cuánto te cuesta.', 'error'); return false; }
      if (!t.name.trim()) { toast.notify('Ponle un nombre.', 'error'); return false; }
      if (t.esPlato && !(Math.round(num(t.servings)) > 0)) { toast.notify('Di cuántas porciones salen.', 'error'); return false; }
      if (!t.esPlato && !(num(t.yieldQty) > 0)) { toast.notify(`Di cuánto rinde ${t.name.trim() ? `«${t.name.trim()}»` : 'la preparación'}.`, 'error'); return false; }
      const invalid = conLineas.findIndex((line) => !line.ingredientName.trim() || !(num(line.qty) > 0) || !line.unit || !line.ingredientId);
      if (invalid >= 0) {
        if (k < tarjetas.length) setTarjeta(k, { abierta: t.lines.indexOf(conLineas[invalid]) });
        toast.notify('Completa la fila marcada antes de guardar.', 'error');
        return false;
      }
    }
    return true;
  };

  const guardar = async (extra?: Tarjeta, opciones: { sinPregunta?: boolean } = {}) => {
    const lista = extra ? [...tarjetas, extra] : tarjetas;
    if (!validar(lista)) return;
    // Solo dictó la masa: «¿Cuántas lleva la porción?» y se crea también el plato.
    if (!extra && !opciones.sinPregunta && !preguntaPorcion && !aMano && tipo !== 'preparacion' && !initialProposal && !targetRecipeId
      && lista.length === 1 && !lista[0].esPlato && !pendientes.length && totalPropuestas === 1) {
      setPreguntaPorcion({ cantidad: '', precio: '' });
      return;
    }
    setGuardando(true);
    const ids: Record<number, string> = {};
    lista.forEach((t, k) => { if (t.guardadaId) ids[k] = t.guardadaId; });
    const creados = new Map<string, string>();
    const nuevosIngredientes: Ingredient[] = [];
    let ultimoPlato: string | undefined;
    try {
      for (const k of ordenDeGuardado(lista)) {
        const t = lista[k];
        if (ids[k]) { if (t.esPlato) ultimoPlato = ids[k]; continue; }
        const conLineas = t.lines.filter((l) => l.ingredientName.trim() || l.ingredientId);
        const precio = num(t.price);
        const input: ConfirmRecipeProposalInput = {
          draftId: t.draftId,
          recipeId: t.recipeId,
          name: t.name.trim(),
          group: t.esPlato ? t.group.trim() || undefined : undefined,
          servings: t.esPlato ? Math.round(num(t.servings)) : 1,
          steps: t.steps,
          isSubrecipe: !t.esPlato,
          yieldQty: t.esPlato ? undefined : num(t.yieldQty),
          yieldUnit: t.esPlato ? undefined : t.yieldUnit,
          ...(!t.esPlato && t.yieldUnit === 'pz' && num(t.unitWeightG) > 0 ? { unitWeightG: num(t.unitWeightG) } : {}),
          ...(t.colchonPropio != null && t.colchonPropio !== '' ? { safetyMarginPct: colchonDe(t) } : {}),
          lines: conLineas.map((line) => {
            const j = tarjetaDe(line);
            const yaCreado = line.ingredientId === NUEVO ? creados.get(normalizeIngredientName(line.ingredientName)) : undefined;
            return {
              ingredientName: line.ingredientName.trim(),
              ingredientId: yaCreado ?? (!line.isSubrecipe && line.ingredientId && line.ingredientId !== NUEVO ? line.ingredientId : undefined),
              recipeId: j != null ? ids[j] : line.isSubrecipe ? line.ingredientId : undefined,
              createNew: line.ingredientId === NUEVO && !yaCreado,
              qty: num(line.qty),
              unit: line.unit,
              wastePct: line.isSubrecipe ? 0 : num(line.wastePct) || 0,
              estimated: (line.estimated && !line.revisada) || undefined,
            };
          }),
        };
        const { recipe, newIngredients } = await confirm.mutateAsync(input);
        ids[k] = recipe.recipeId;
        if (k < tarjetas.length) setTarjeta(k, { guardadaId: recipe.recipeId });
        newIngredients.forEach((ing) => { creados.set(normalizeIngredientName(ing.name), ing.ingredientId); nuevosIngredientes.push(ing); });
        // «Sí, es un plato de mi carta» con precio: va también a la carta.
        if (t.esPlato && precio > 0 && !t.recipeId) {
          try {
            await saveMenuItem.mutateAsync({ input: { recipeId: recipe.recipeId, name: recipe.name, sellingPriceGross: precio, vatPct: settings?.vatPct ?? 16, targetFoodCostPct: 30, active: true, ...(t.group.trim() ? { group: t.group.trim() } : {}) } });
          } catch (error) {
            toast.notify(`Se guardó el plato, pero no su precio: ${error instanceof Error ? error.message : 'inténtalo desde Tu carta'}.`, 'error');
          }
        }
        if (t.esPlato) ultimoPlato = recipe.recipeId;
      }
    } catch (error) {
      toast.fail(error);
      setGuardando(false);
      return;
    }
    setGuardando(false);
    setPreguntaPorcion(null);
    // Fase 7: lo que el usuario emparejó a mano no se vuelve a preguntar.
    const aprender = lista.flatMap((t) => t.lines).filter((l) => debeRecordar(l, ingredients)).map((l) => ({ ingredientId: l.ingredientId, text: l.dicho!.trim(), source: 'dictado' as const }));
    if (aprender.length) {
      api.rememberIngredientAliases(aprender)
        .then(() => qc.invalidateQueries({ queryKey: ['ingredients'] }))
        .catch((e) => console.warn('[dictado] no se pudo recordar', e));
    }
    const preps = lista.filter((t) => !t.esPlato).length;
    const platos = lista.length - preps;
    const partes = [preps ? contar(preps, 'preparación', 'preparaciones') : '', platos ? contar(platos, 'plato', 'platos') : ''].filter(Boolean).join(' y ');
    toast.success(`${lista.length > 1 ? `Guardé ${partes}` : `${platos ? 'Plato' : 'Preparación'} guardad${platos ? 'o' : 'a'}`}${nuevosIngredientes.length ? `, con ${contar(nuevosIngredientes.length, 'ingrediente', 'ingredientes')} por confirmar` : ''}.`);
    if (pendientes.length) {
      // Lo recién guardado ya se puede usar en lo que viene.
      const [next, ...rest] = pendientes;
      const hechas = { ...guardadas };
      const base = next.indice - tarjetas.length;
      lista.forEach((_, k) => { if (ids[k]) hechas[base + k] = ids[k]; });
      setGuardadas(hechas);
      setPendientes(rest);
      const catalog = [...ingredients, ...nuevosIngredientes];
      setTarjetas([aTarjeta(next.proposal, planRecipeProposal(next.proposal, catalog), { draftId: next.draftId, base: next.indice, hechas }, 0)]);
    } else onSaved(ultimoPlato ?? ids[0] ?? Object.values(ids)[0]);
  };

  /** «¿Cuántas lleva la porción?» → la tarjeta del plato que sale de la preparación. */
  const guardarConPlato = () => {
    if (!preguntaPorcion) return;
    const prep = tarjetas[0];
    const cantidad = num(preguntaPorcion.cantidad);
    if (!(cantidad > 0)) { toast.notify('Di cuántas lleva la porción.', 'error'); return; }
    const unidad = unidadDeUso(prep.yieldUnit);
    const plato: Tarjeta = {
      ...tarjetaVacia(true, 'porcion'),
      name: platoDesdePreparacion(prep.name, cantidad, unidad),
      price: preguntaPorcion.precio,
      lines: [{ ...lineaVacia(), ingredientId: `${CARD}0`, isSubrecipe: true, ingredientName: prep.name, qty: formatDecimalInput(cantidad), unit: unidad, revisada: true }],
    };
    void guardar(plato);
  };

  const gruposCarta = (() => {
    const propios = [...new Set([...recipes.filter((r) => r.kind !== 'subreceta').map((r) => r.group), ...menuItems.map((m) => m.group)]
      .map((g) => g?.trim()).filter((g): g is string => Boolean(g)))].sort((a, b) => a.localeCompare(b));
    return propios.length ? propios : GRUPOS_DE_SERIE;
  })();
  const indice = totalPropuestas - pendientes.length;
  const variasALaVez = tarjetas.length > 1;
  const resumen = (() => {
    const partes: string[] = [];
    const preps = tarjetas.filter((t) => !t.esPlato).length;
    const actualiza = tarjetas.filter((t) => t.esPlato && t.recipeId).length;
    const enCarta = tarjetas.filter((t) => t.esPlato && !t.recipeId && num(t.price) > 0).length + (preguntaPorcion && num(preguntaPorcion.precio) > 0 ? 1 : 0);
    const sinPrecio = tarjetas.filter((t) => t.esPlato && !t.recipeId && !(num(t.price) > 0)).length + (preguntaPorcion && !(num(preguntaPorcion.precio) > 0) ? 1 : 0);
    if (preps) partes.push(contar(preps, 'preparación', 'preparaciones'));
    if (actualiza) partes.push('la receta de tu plato');
    if (enCarta) partes.push(`${contar(enCarta, 'plato', 'platos')} en tu carta`);
    if (sinPrecio) partes.push(contar(sinPrecio, 'plato', 'platos'));
    const nuevos = [...new Set(tarjetas.flatMap((t) => t.lines.filter((l) => l.ingredientId === NUEVO && l.ingredientName.trim()).map((l) => l.ingredientName.trim().toLowerCase())))];
    return { partes, nuevos, plural: preps + actualiza + enCarta + sinPrecio + nuevos.length > 1 };
  })();
  const etiquetaGuardar = guardando || confirm.isPending ? 'Guardando…'
    : variasALaVez ? `Guardar los ${PALABRAS[tarjetas.length] ?? tarjetas.length}`
    : pendientes.length ? `Guardar y seguir (${indice} de ${totalPropuestas})` : 'Guardar';
  const unaSola = tarjetas[0];
  const titulo = !enPaso2 ? (tipo === 'preparacion' ? 'Cuéntame tu preparación' : 'Cuéntame tu plato')
    : aMano ? (unaSola?.esPlato ? 'Tu plato' : 'Tu preparación')
    : unaSola?.marcadoPorAlexia ? 'Revisa lo que entendí' : unaSola?.esPlato ? 'Tu plato' : 'Tu preparación';

  const entendi = (() => {
    if (!variasALaVez) return totalPropuestas > 1 ? <>Entendí <strong>{totalPropuestas} cosas</strong>. Las revisamos una a una y se guardan por orden: esta es la {indice}.</> : null;
    const prep = tarjetas.find((t) => !t.esPlato);
    const plato = tarjetas.find((t) => t.esPlato);
    const palabra = PALABRAS[tarjetas.length] ?? String(tarjetas.length);
    if (tarjetas.length === 2 && prep && plato) {
      return <>Entendí <strong>dos cosas</strong>: una preparación que haces en cantidad ({prep.name.toLowerCase()}) y un plato que vendes ({plato.name}). Revisa las dos y guarda.</>;
    }
    return <>Entendí <strong>{palabra} cosas</strong>. Revisa {tarjetas.length === 2 ? 'las dos' : `las ${palabra}`} y guarda.</>;
  })();

  const renderTarjeta = (t: Tarjeta, k: number) => {
    const v = vivo[k] ?? { porLinea: new Map<number, number>(), importantes: [], menores: [] };
    const cost = v.cost;
    const colchon = colchonDe(t);
    const teCuesta = cost ? (t.esPlato ? cost.costPerPortion : cost.totalBatchCost) : undefined;
    const precio = num(t.price);
    const vat = settings?.vatPct ?? 16;
    const neto = precio > 0 ? precio / (1 + vat / 100) : 0;
    const teDeja = precio > 0 && teCuesta != null ? neto - commissionOnGross(precio, settings?.bankCommissionPct ?? 0) - teCuesta : undefined;
    const tePct = teDeja != null && neto > 0 ? (teDeja / neto) * 100 : undefined;
    const minimo = settings?.alerts.minGrossMarginPct ?? 65;
    const incompleto = v.importantes.length > 0;
    const aproximado = !incompleto && v.menores.length > 0;
    const sufijo = k === 0 ? '' : `-${k}`;
    const hayPlato = tarjetas.some((x) => x.esPlato);
    const lineasConOtras = [...t.lines.map((l) => l.ingredientName), ...t.lines.flatMap((l) => { const j = tarjetaDe(l); return j != null && tarjetas[j] ? tarjetas[j].lines.map((x) => x.ingredientName) : []; })];
    const sugerencias = (t.esPlato || !hayPlato)
      ? sugerenciasPara({ texto: aMano ? undefined : textoFuente, nombre: t.name, lineas: lineasConOtras }).filter((s) => !t.sugerenciasVistas.includes(s))
      : [];
    // Mientras se escribe el grupo nuevo no se pinta como chip: si no, la primera letra se volvía chip y vaciaba el campo (Juan, 5 nov 2026).
    const grupos = !t.creandoGrupo && t.group.trim() && !gruposCarta.includes(t.group.trim()) ? [...gruposCarta, t.group.trim()] : gruposCarta;
    const otrasPreps = tarjetas.map((x, j) => ({ x, j })).filter(({ x, j }) => j !== k && !x.esPlato);
    const opcionesCatalogo = [
      ...otrasPreps.map(({ x, j }) => ({ value: `${CARD}${j}`, label: `${x.name || 'Preparación de arriba'} · preparación de arriba` })),
      ...ingredients.filter((i) => i.active).map((i) => ({ value: i.ingredientId, label: [i.name, ingredientDetails(i, suppliers)].filter(Boolean).join(' · ') })),
      ...recipes.filter((r) => r.active && r.kind === 'subreceta').map((r) => ({ value: `rec:${r.recipeId}`, label: `${r.name} · preparación` })),
    ];

    return (
      <section key={t.clave} className={`nuevo-plato__tarjeta${variasALaVez ? ' nuevo-plato__tarjeta--varias' : ''}`} aria-label={variasALaVez ? t.name || `Receta ${k + 1}` : undefined}>
        {variasALaVez && <h3 className="nuevo-plato__tarjeta-titulo"><span aria-hidden="true">{k + 1}</span>{t.name || (t.esPlato ? 'Plato' : 'Preparación')}{t.guardadaId && <small> · guardada</small>}</h3>}

        <div className="field">
          <label className="field__label" htmlFor={`nuevo-plato-nombre${sufijo}`}>{t.esPlato ? 'Nombre en tu carta' : 'Nombre'}</label>
          <input id={`nuevo-plato-nombre${sufijo}`} className="input" value={t.name} onChange={(e) => setTarjeta(k, { name: e.target.value })} placeholder={t.esPlato ? 'Tacos de camarón' : 'Salsa brava'} />
        </div>

        {!t.recipeId && (
          <fieldset className="nuevo-plato__vende">
            <legend>¿Lo vendes tal cual en tu carta?</legend>
            <div className="nuevo-plato__opciones">
              <label className={`nuevo-plato__opcion${t.esPlato ? ' is-active' : ''}`}><input type="radio" name={`vende${sufijo}`} checked={t.esPlato} onChange={() => setTarjeta(k, { esPlato: true })} /> Sí, es un plato de mi carta</label>
              <label className={`nuevo-plato__opcion${!t.esPlato ? ' is-active' : ''}`}><input type="radio" name={`vende${sufijo}`} checked={!t.esPlato} onChange={() => setTarjeta(k, { esPlato: false })} /> No, la uso dentro de otros platos</label>
            </div>
            {t.marcadoPorAlexia && (
              <p className="nuevo-plato__porque">
                {t.porque ? <>Lo marqué porque dijiste «{t.porque}».</> : 'Lo marqué por lo que contaste.'}
                {!t.esPlato && ' Va a Preparaciones, sin precio de venta.'}
              </p>
            )}
          </fieldset>
        )}

        {!t.esPlato && (
          <div className="nuevo-plato__datos nuevo-plato__datos--rinde">
            <label className="field">
              <span className="field__label">Rinde<QueEs termino="rinde" /></span>
              <input className="input" inputMode="decimal" value={t.yieldQty} onChange={(e) => setTarjeta(k, { yieldQty: e.target.value })} placeholder="400" />
            </label>
            <UnitSelectField label="Unidad" value={t.yieldUnit} onChange={(value) => setTarjeta(k, { yieldUnit: value as Unit })} />
            {t.yieldUnit === 'pz' && (
              <label className="field">
                <span className="field__label">Cada pieza pesa</span>
                <span className="input-suffix"><input className="input" inputMode="decimal" aria-label="Cada pieza pesa (opcional)" value={t.unitWeightG} onChange={(e) => setTarjeta(k, { unitWeightG: e.target.value })} placeholder="opcional" /><span>g</span></span>
              </label>
            )}
          </div>
        )}

        <div className="nuevo-plato__ingredientes">
          {t.lines.length > 0 && (
            <>
              <p className="nuevo-plato__explica">
                {t.marcadoPorAlexia && t.lines.some((l) => l.rawText) ? 'Cada cosa que dijiste la emparejé con uno de tus ingredientes. Si no es, tócalo y elige otro.' : 'Lo que lleva, con la cantidad limpia que entra en la receta.'}
              </p>
              <div className="filas-ing__cabecera" aria-hidden="true"><span>{t.esPlato && t.lines.some((l) => l.isSubrecipe) ? 'Lleva' : 'Ingrediente'}</span><span>Cantidad</span><span>Costo</span></div>
            </>
          )}
          <ul className="filas-ing">
            {t.lines.map((line, index) => {
              const revisar = necesitaRevision(line);
              const deOtra = tarjetaDe(line);
              const estado = line.ingredientId === NUEVO ? '+' : revisar ? '?' : '✓';
              const catalogo = line.isSubrecipe ? undefined : ingredients.find((i) => i.ingredientId === line.ingredientId);
              const costo = v.porLinea.get(index);
              const detalle = deOtra != null ? 'Preparación · la de arriba'
                : line.isSubrecipe ? (line.yaLaTenias ? 'Preparación · ya la tenías' : 'Preparación')
                : catalogo ? ingredientDetails(catalogo, suppliers)
                : line.ingredientId === NUEVO ? 'No lo tienes: se crea «por confirmar», sin precio' : '';
              const abiertaEsta = t.abierta === index || (t.abierta === null && revisar && t.lines.findIndex(necesitaRevision) === index);
              const nombre = deOtra != null ? tarjetas[deOtra]?.name || line.ingredientName : catalogo?.name || line.ingredientName || 'Nuevo ingrediente';
              return (
                <li key={index} className={`filas-ing__fila${revisar ? ' is-revisar' : ''}`}>
                  <button type="button" className="filas-ing__resumen" aria-expanded={abiertaEsta} onClick={() => setTarjeta(k, { abierta: abiertaEsta ? -1 : index })}>
                    <span className={`filas-ing__estado filas-ing__estado--${estado === '✓' ? 'ok' : estado === '+' ? 'nuevo' : 'duda'}`} aria-label={estado === '✓' ? 'Emparejado' : estado === '+' ? 'Nuevo' : 'Por revisar'}>{estado}</span>
                    <span className="filas-ing__nombre">
                      <strong>{nombre}</strong>
                      <small>{line.rawText && deOtra == null ? <>Dijiste «{line.rawText}»{detalle ? ` · ${detalle}` : ''}</> : detalle}</small>
                    </span>
                    <span className="filas-ing__cant">
                      {num(line.qty) > 0 ? (line.unit === 'pz' && line.isSubrecipe ? `${formatQty(num(line.qty))} ${num(line.qty) === 1 ? 'pieza' : 'piezas'}` : formatQty(num(line.qty), line.unit)) : <em>¿Cuánto?</em>}
                      {line.estimated && !line.revisada && num(line.qty) > 0 && <small>{line.propuesta ? '(cantidad propuesta)' : line.supuseUnidad ? `Supuse ${line.unit === 'ml' ? 'mililitros' : line.unit === 'g' ? 'gramos' : unitLabel(line.unit)}` : 'Calculado: revísalo'}</small>}
                    </span>
                    <span className="filas-ing__costo">{costo != null && Number.isFinite(costo) ? formatMoney(costo) : '—'}</span>
                  </button>
                  {abiertaEsta && (
                    <div className="filas-ing__editor">
                      {line.match.kind === 'ambiguous' && !line.revisada && (
                        <div className="filas-ing__dudas">
                          <span>¿Cuál de tus ingredientes es «{line.ingredientName}»?</span>
                          {line.match.candidates.map((c) => (
                            <button key={c.ingredientId} type="button" className={`chip${line.ingredientId === c.ingredientId ? ' is-active' : ''}`} onClick={() => elegir(k, index, line.isSubrecipe ? `rec:${c.ingredientId}` : c.ingredientId)}>{c.name}</button>
                          ))}
                        </div>
                      )}
                      {deOtra == null && (
                        <div className="field filas-ing__buscar">
                          <span className="field__label">{line.isSubrecipe ? 'Preparación' : 'Ingrediente'}</span>
                          <SmartSelect
                            value={line.isSubrecipe && line.ingredientId ? `rec:${line.ingredientId}` : line.ingredientId}
                            ariaLabel="Cambiar el ingrediente de esta fila"
                            placeholder="Busca en tus ingredientes…"
                            buscable
                            onChange={(value) => elegir(k, index, value)}
                            options={[
                              { value: '', label: 'Elige…' },
                              { value: NUEVO, label: `+ Crear «${line.ingredientName || 'ingrediente nuevo'}» como nuevo`, fija: true },
                              ...opcionesCatalogo.filter((o) => !o.value.startsWith(CARD)),
                            ]}
                          />
                        </div>
                      )}
                      {line.ingredientId === NUEVO && (
                        <label className="field">
                          <span className="field__label">Nombre del ingrediente nuevo</span>
                          <input className="input" value={line.ingredientName} onChange={(e) => setLine(k, index, { ingredientName: e.target.value })} />
                        </label>
                      )}
                      <div className={`filas-ing__cantidad${line.isSubrecipe ? ' filas-ing__cantidad--dos' : ''}`}>
                        <label className="field">
                          <span className="field__label">{line.isSubrecipe ? 'Lleva' : 'Entra en la receta (limpia)'}</span>
                          <input className="input" inputMode="decimal" value={line.qty} onChange={(e) => setLine(k, index, { qty: e.target.value })} />
                        </label>
                        <UnitSelectField label="Unidad" value={line.unit} onChange={(value) => setLine(k, index, { unit: value as Unit })} />
                        {!line.isSubrecipe && (
                          <label className="field">
                            <span className="field__label">Se tira al limpiar<QueEs termino="seTiraAlLimpiar" /></span>
                            <span className="input-suffix"><input className="input" inputMode="decimal" value={line.wastePct} onChange={(e) => setLine(k, index, { wastePct: e.target.value })} /><span>%</span></span>
                          </label>
                        )}
                      </div>
                      {ofrecerRecordar(line, ingredients) && (
                        <label className="filas-ing__recordar">
                          <input type="checkbox" checked={line.recordar !== false} onChange={(e) => setLine(k, index, { recordar: e.target.checked })} />
                          La próxima vez que digas «{line.dicho}», usar {ingredients.find((i) => i.ingredientId === line.ingredientId)?.name ?? 'este'} sin preguntar
                        </label>
                      )}
                      {line.conversionNote && <p className="filas-ing__calculo">{line.conversionNote}. Al costo va lo que compras.</p>}
                      {!line.isSubrecipe && <p className="muted small">Lo que se evapora al cocinar no cuenta como «se tira».</p>}
                      <div className="filas-ing__acciones">
                        {revisar && line.ingredientId && num(line.qty) > 0 && <button type="button" className="btn btn--sm" onClick={() => { setLine(k, index, { revisada: true, estimated: false }); setTarjeta(k, { abierta: -1 }); }}>Está bien así</button>}
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => removeLine(k, index)}>Quitar</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="nuevo-plato__buscar-nuevo">
            <SmartSelect
              value=""
              buscable
              ariaLabel="Buscar en tus ingredientes y preparaciones"
              placeholder="Busca un ingrediente o preparación…"
              onSearch={(value) => setTarjeta(k, { busqueda: value })}
              onChange={(value) => agregarDesdeBuscador(k, value)}
              options={[
                ...opcionesCatalogo,
                { value: NUEVO, label: t.busqueda.trim() ? `+ Crear «${t.busqueda.trim()}» como ingrediente nuevo` : '+ Crear ingrediente nuevo', fija: true },
              ]}
            />
          </div>
        </div>

        {sugerencias.map((s) => {
          const d = sugerido[s];
          return (
            <div key={s} className="nuevo-plato__sugerencia" role="group" aria-label={s === 'aceite' ? '¿Las fríes?' : '¿Lleva pan?'}>
              <strong>{s === 'aceite' ? '¿Las fríes?' : '¿Lleva pan?'}</strong>
              <span>
                {s === 'aceite' ? 'No mencionaste el aceite.' : 'No mencionaste el pan.'}
                {d.costo != null && d.costo > 0 ? ` Suele salir a unos ${formatMoney(d.costo)} por porción.` : ''}
              </span>
              <div className="nuevo-plato__sugerencia-botones">
                <button type="button" className="btn btn--sm" onClick={() => aplicarSugerencia(k, s)}>{s === 'aceite' ? 'Agregar aceite' : 'Agregar pan'}</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setTarjeta(k, { sugerenciasVistas: [...t.sugerenciasVistas, s] })}>No hace falta</button>
              </div>
            </div>
          );
        })}

        {t.esPlato && (
          <>
            <div className="nuevo-plato__datos nuevo-plato__datos--plato">
              {!t.recipeId && (
                <label className="field">
                  <span className="field__label">Lo vendes a <span className="muted">(con IVA, en el local)</span></span>
                  <input className="input" inputMode="decimal" value={t.price} onChange={(e) => setTarjeta(k, { price: e.target.value })} onBlur={() => { const n = num(t.price); if (n > 0) setTarjeta(k, { price: formatMoneyInput(n) }); }} placeholder="0" />
                </label>
              )}
              <label className="field">
                <span className="field__label">Porciones que salen</span>
                <input className="input" inputMode="numeric" value={t.servings} onChange={(e) => setTarjeta(k, { servings: e.target.value })} />
              </label>
            </div>
            <div className="field nuevo-plato__grupos">
              <span className="field__label" id={`nuevo-plato-grupo${sufijo}`}>Grupo de tu carta</span>
              <div className="nuevo-plato__grupos-chips" role="group" aria-labelledby={`nuevo-plato-grupo${sufijo}`}>
                {grupos.map((g) => (
                  <button key={g} type="button" className={`nuevo-plato__chip${t.group.trim() === g ? ' is-active' : ''}`} aria-pressed={t.group.trim() === g} onClick={() => setTarjeta(k, { group: t.group.trim() === g ? '' : g, creandoGrupo: false })}>{g}</button>
                ))}
                {t.creandoGrupo ? (
                  <input className="input nuevo-plato__grupo-nuevo" aria-label="Nombre del grupo nuevo" autoFocus value={t.group} onChange={(e) => setTarjeta(k, { group: e.target.value })}
                    onBlur={() => setTarjeta(k, { creandoGrupo: false, group: t.group.trim() })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                    placeholder="Entradas" />
                ) : (
                  <button type="button" className="nuevo-plato__chip nuevo-plato__chip--crear" onClick={() => setTarjeta(k, { creandoGrupo: true, group: '' })}>+ Crear grupo</button>
                )}
              </div>
            </div>
          </>
        )}

        <div className="nuevo-plato__costo">
          {teCuesta != null && !(incompleto && !(teCuesta > 0)) ? (
            <>
              <div className="nuevo-plato__cifra">
                <span>{t.esPlato ? 'Te cuesta' : 'Te cuesta toda'}<QueEs termino={t.esPlato ? 'teCuesta' : 'teCuestaTanda'} palabra={t.esPlato ? undefined : 'Te cuesta toda'} /></span>
                <strong>{formatMoney(teCuesta)}</strong>
                {incompleto ? <small>sin contar {listaConArticulos(v.importantes)}</small>
                  : !t.esPlato && cost && num(t.yieldQty) > 0 ? <small>{formatMoney(cost.totalBatchCost / num(t.yieldQty))} por {t.yieldUnit === 'pz' ? 'pieza' : unitLabel(t.yieldUnit)}</small>
                  : t.esPlato ? <small>por porción</small> : null}
              </div>
              {/* Sin un precio importante, no hay «te deja»: sería falso (punto 2 del 12 oct). */}
              {t.esPlato && !incompleto && teDeja != null && tePct != null && (
                <div className={`nuevo-plato__cifra nuevo-plato__cifra--${tePct >= minimo ? 'bien' : 'aten'}`}>
                  <span>Te deja en el local{aproximado ? ' · aproximado' : ''}<QueEs termino="teDeja" /></span>
                  <strong>{formatMoney(teDeja)} · {formatPct(tePct, 0)}</strong>
                  <small>{tePct >= minimo ? `Llega a tu mínimo (${formatPct(minimo, 0)})` : `No llega a tu mínimo (${formatPct(minimo, 0)})`}</small>
                </div>
              )}
            </>
          ) : !incompleto ? <p className="muted">Agrega ingredientes con su cantidad y aquí verás cuánto te cuesta.</p> : null}
          {incompleto && <p className="nuevo-plato__falta">Incompleto: falta el precio {deLa(listaConArticulos(v.importantes))}.</p>}
          {aproximado && <p className="nuevo-plato__falta">Aproximado<QueEs termino="aproximado" />: falta {listaConArticulos(v.menores)}.</p>}
          {cost?.status === 'estimated' && !incompleto && !aproximado && <p className="nuevo-plato__falta">Aproximado<QueEs termino="aproximado" />: algún precio lo puso Alexia.</p>}
          <p className="muted small">Incluye tu colchón para imprevistos ({formatPct(colchon, 0)}{t.colchonPropio == null ? ', en Configuración' : ', solo para este plato'}).</p>
        </div>

        <details className="nuevo-plato__mas">
          <summary>Más opciones <span className="muted">· colchón para imprevistos<QueEs termino="colchon" /></span></summary>
          {t.colchonPropio == null ? (
            <p>Usa el de tu cuenta, {formatPct(colchonCuenta, 0)}. <button type="button" className="nuevo-plato__enlace" onClick={() => setTarjeta(k, { colchonPropio: formatDecimalInput(colchonCuenta) })}>Cambiarlo solo para {t.esPlato ? 'este plato' : 'esta preparación'}</button></p>
          ) : (
            <label className="field filas-ing__num">
              <span className="field__label">Colchón para imprevistos de {t.esPlato ? 'este plato' : 'esta preparación'}</span>
              <span className="input-suffix"><input className="input" inputMode="decimal" value={t.colchonPropio} onChange={(e) => setTarjeta(k, { colchonPropio: e.target.value })} /><span>%</span></span>
              <button type="button" className="nuevo-plato__enlace" onClick={() => setTarjeta(k, { colchonPropio: null })}>Usar el de mi cuenta</button>
            </label>
          )}
          <p className="muted small">El código se pone solo. Puedes cambiarlo después en la ficha.</p>
        </details>
      </section>
    );
  };

  const prepUnica = tarjetas[0];
  const unidadPorcion = unidadDeUso(prepUnica?.yieldUnit);

  return (
    <Drawer
      open
      title={titulo}
      subtitle={enPaso2 ? undefined : 'Como se lo explicarías a alguien de tu cocina. Alexia lo ordena y en el paso 2 lo revisas.'}
      onClose={closeDrawer}
      width="38rem"
      footer={enPaso2 ? (preguntaPorcion ? (
        <div className="nuevo-plato__pie nuevo-plato__pie--paso2 nuevo-plato__porcion" role="group" aria-label="Crear también el plato">
          <p className="nuevo-plato__alexia"><span aria-hidden="true">a</span><span>Y para venderla, <strong>¿cuántas lleva la porción?</strong> Creo también el plato de tu carta con {prepUnica?.name ? `«${prepUnica.name}»` : 'la preparación'}.</span></p>
          <div className="nuevo-plato__datos nuevo-plato__datos--porcion">
            <label className="field">
              <span className="field__label">¿Cuántas lleva la porción?</span>
              <span className="input-suffix"><input className="input" inputMode="decimal" autoFocus value={preguntaPorcion.cantidad} onChange={(e) => setPreguntaPorcion({ ...preguntaPorcion, cantidad: e.target.value })} placeholder="8" /><span>{unidadPorcion === 'pz' ? 'piezas' : unitLabel(unidadPorcion)}</span></span>
            </label>
            <label className="field">
              <span className="field__label">Lo vendes a <span className="muted">(con IVA)</span></span>
              <input className="input" inputMode="decimal" value={preguntaPorcion.precio} onChange={(e) => setPreguntaPorcion({ ...preguntaPorcion, precio: e.target.value })} placeholder="0" />
            </label>
          </div>
          <div className="nuevo-plato__pie-botones">
            <button type="button" className="btn" disabled={guardando} onClick={guardarConPlato}>{guardando ? 'Guardando…' : 'Guardar los dos'}</button>
            {!requireIngredients && <button type="button" className="nuevo-plato__volver" disabled={guardando} onClick={() => void guardar(undefined, { sinPregunta: true })}>Solo la preparación</button>}
          </div>
        </div>
      ) : (
        <div className="nuevo-plato__pie nuevo-plato__pie--paso2">
          {resumen.partes.length > 0 && (
            <p className="nuevo-plato__resumen">
              Al guardar se crea{resumen.plural ? 'n' : ''}{' '}
              {resumen.partes.map((p, i) => <span key={p}>{i > 0 ? (i === resumen.partes.length - 1 && !resumen.nuevos.length ? ' y ' : ', ') : ''}<strong>{p}</strong></span>)}
              {resumen.nuevos.length > 0 && <> y <strong className="nuevo-plato__resumen-nuevos">{contar(resumen.nuevos.length, 'ingrediente', 'ingredientes')} por confirmar</strong> ({resumen.nuevos.join(', ')})</>}.
            </p>
          )}
          <div className="nuevo-plato__pie-botones">
            <button type="button" className="btn" disabled={guardando || confirm.isPending || saveMenuItem.isPending} onClick={() => void guardar()}>{etiquetaGuardar}</button>
            {!initialProposal && <button type="button" className="nuevo-plato__volver" onClick={volverAlPaso1}>{aMano ? 'Contárselo a Alexia' : 'Volver a contarlo'}</button>}
          </div>
        </div>
      )) : mode !== 'photo' ? (
        <div className="nuevo-plato__pie nuevo-plato__pie--paso1">
          <button type="button" className="btn btn--lg" disabled={text.trim().length < 10 || propose.isPending || dictation.listening || dictation.transcribing} onClick={() => generate()}>
            {propose.isPending ? `Ordenando tu ${tipo === 'preparacion' ? 'preparación' : 'plato'}…` : 'Ver lo que entendió Alexia'}
          </button>
          <span>Todavía no se guarda nada.</span>
        </div>
      ) : undefined}
    >
      {!enPaso2 ? (
        <div className="stack nuevo-plato nuevo-plato--paso1">
          <p className="nuevo-plato__paso">Paso 1 de 2</p>
          <div role="tablist" aria-label="Cómo contarlo" className="segmentos nuevo-plato__modos">
            {([...(dictation.supported ? [['dictate', 'Dictar']] : []), ['text', 'Escribir'], ['photo', 'Foto']] as const).map(([value, label]) => (
              <button key={value} type="button" role="tab" aria-selected={mode === value} className={`segmentos__item${mode === value ? ' is-active' : ''}`} onClick={() => { dictation.stop(); setMode(value as typeof mode); }}>{label}</button>
            ))}
          </div>

          {mode === 'dictate' && (
            <section className={`nuevo-plato__dictar${dictation.listening ? ' is-recording' : ''}`}>
              <button
                type="button"
                className="nuevo-plato__mic"
                onClick={dictation.listening ? dictation.stop : dictation.start}
                disabled={propose.isPending || dictation.transcribing}
                aria-pressed={dictation.listening}
              >
                <span className="nuevo-plato__mic-circulo" aria-hidden="true"><MicIcon /></span>
                <span className="nuevo-plato__mic-texto">{dictation.listening ? 'Terminar' : dictation.transcribing ? 'Escribiendo lo que dijiste…' : 'Toca y habla'}</span>
              </button>
              <p className="nuevo-plato__ayuda">
                {dictation.listening
                  ? `Te escucho${dictation.seconds != null ? ` · ${dictation.seconds} s de ${dictation.maxSeconds}` : ''}`
                  // Desde Preparaciones no se vende: sin «a cuánto» (14 oct 2026).
                  : tipo === 'preparacion'
                    ? 'Di el nombre, cuánto rinde y los ingredientes con sus cantidades.'
                    : 'Di el nombre, cuántas porciones salen (o cuánto rinde) y los ingredientes con sus cantidades. Si lo vendes, di también a cuánto.'}
              </p>
              {dictation.error && <p role="alert" className="nuevo-plato__error">{dictation.error}</p>}
            </section>
          )}

          {mode === 'photo' && (
            <section className="nuevo-plato__fotos">
              <div className="nuevo-plato__subir">
                <strong>Sube una foto o un PDF</strong>
                <div className="nuevo-plato__subir-botones">
                  <label className="btn nuevo-plato__archivo">
                    Tomar una foto
                    <input type="file" accept="image/*" capture="environment" hidden disabled={photo.isPending || readingPhoto || propose.isPending} onChange={(event) => { const file = event.target.files?.[0]; if (file) void readPhoto(file); event.target.value = ''; }} />
                  </label>
                  <label className="btn btn--ghost nuevo-plato__archivo nuevo-plato__foto">
                    Elegir archivo
                    <input type="file" accept={ACCEPT_ESCANER} hidden disabled={photo.isPending || readingPhoto || propose.isPending} onChange={(event) => { const file = event.target.files?.[0]; if (file) void readPhoto(file); event.target.value = ''; }} />
                  </label>
                </div>
                <p className="nuevo-plato__ayuda">Foto o PDF · hasta 8 MB</p>
              </div>
              <ul className="nuevo-plato__usos">
                <li><strong>La receta de un plato</strong><span>Una ficha, una libreta, una nota del celular. Leo ingredientes y cantidades.</span></li>
                <li><strong>Tu carta entera</strong><span>Creo tus platos con su precio, de uno en uno. Las recetas se las pones después, cuando puedas.</span></li>
              </ul>
              {(photo.isPending || readingPhoto) && <p role="status">Leyendo tu foto…</p>}
            </section>
          )}

          {mode !== 'photo' && (
            <div className="field">
              <label className="field__label" htmlFor="alexia-recipe-text">
                {mode === 'dictate' ? <>Lo que dijiste <span className="muted">· puedes corregirlo aquí</span></> : tipo === 'preparacion' ? 'Tu preparación, con tus palabras' : 'Tu plato, con tus palabras'}
              </label>
              <textarea
                id="alexia-recipe-text"
                className="input"
                rows={6}
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder={tipo === 'preparacion' ? EJEMPLO_PREP : ejemploDePlato(moneda)}
              />
              {mode === 'text' && <p className="field__hint">No hace falta orden ni palabras perfectas. Lo que falte, te lo pregunto en el paso 2.</p>}
            </div>
          )}
          {isDemoMode && <p className="nuevo-plato__demo">Demo: al enviar cargaremos una propuesta de ejemplo (si hablas de croquetas, verás cómo separo la masa y el plato).</p>}

          <button type="button" className="nuevo-plato__yo" disabled={propose.isPending || photo.isPending || readingPhoto || dictation.listening} onClick={llenarlaYo}>
            Prefiero llenarlo yo, sin Alexia
          </button>
        </div>
      ) : (
        <div className="stack nuevo-plato nuevo-plato--paso2">
          {!aMano && <p className="nuevo-plato__paso">Paso 2 de 2</p>}
          {entendi && <p className="nuevo-plato__alexia"><span aria-hidden="true">a</span><span>{entendi}</span></p>}
          {tarjetas.map(renderTarjeta)}
        </div>
      )}
    </Drawer>
  );
}
